import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPreference, useUserPreferencesPrefetch } from './useUserPreference';
import { useCoreLanguageSync } from './useCoreLanguageSync';

/**
 * Mounted once in the authenticated app shell. Performs the initial
 * prefetch of all preferences and applies any "live" preference effects
 * — `theme` (toggles the `dark` class on `<html>`) and `density`
 * (toggles `density-compact` on `<body>`).
 *
 * Theme/density apply BEFORE the user clicks Save in the settings page —
 * the optimistic update in `useUserPreference` updates the cache, this
 * effect picks the new value up, and the class flips immediately.
 *
 * #894 P3-041 followup — previously set `theme-light` / `theme-dark` /
 * `theme-system` on `<body>`. Nobody read those classes, and Tailwind's
 * `dark:` utilities + the `.dark` CSS variable overrides in
 * `globals.css` never engaged. Result: the theme toggle in Settings was
 * visually a no-op. The fix routes the resolved theme to the `dark`
 * class on `<html>` — the surface Tailwind's `darkMode: ['class']`
 * strategy expects (see `tailwind.config.ts`).
 */
export function UserPreferencesProvider({ children }: { children: ReactNode }): JSX.Element {
  useUserPreferencesPrefetch();
  // Read-on-boot language sync from Core (W22-F1-S3). Fire-and-forget; never
  // blocks render and silently no-ops if the Core proxy is unreachable.
  useCoreLanguageSync();
  const { i18n } = useTranslation();
  const [theme] = useUserPreference('theme', 'system');
  const [density] = useUserPreference('density', 'comfortable');

  useEffect(() => {
    const root = document.documentElement;
    // Resolve `system` against the OS preference at this moment. We do
    // NOT subscribe to OS preference changes — the user explicitly
    // picked `system` as their stored setting; on the next render or
    // page reload the new OS value is picked up. The cost of a media
    // listener for an edge case isn't worth the lines.
    const wantsDark =
      theme === 'dark' ||
      (theme === 'system' &&
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    root.classList.toggle('dark', !!wantsDark);
  }, [theme]);

  useEffect(() => {
    const body = document.body;
    body.classList.remove('density-comfortable', 'density-compact');
    body.classList.add(`density-${density}`);
  }, [density]);

  useEffect(() => {
    // W22-F1-S3 — keep `<html lang>` aligned with the active i18next language
    // so screen readers and Playwright selectors can pick up the locale.
    document.documentElement.lang = i18n.language || 'en';
  }, [i18n.language]);

  return <>{children}</>;
}
